import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getDateChunk, isEmpty } from 'src/utils'
import PropTypes from 'prop-types'
import Message from 'src/components/Message/Message'
import TextDivider from 'src/components/TextDivider/TextDivider'
import { Box, CircularProgress, styled } from '@mui/material'
import siteConfig from 'src/config/fw-ai/site.config.json'
const { sections } = siteConfig

const StreamContainer = styled(Box, {
  name: 'MessagesStream',
  label: 'messagesStreamContainer',
  overridesResolver: (props, styles) => [styles.container]
})({
  overflowY: 'auto',
  msOverflowStyle: 'none', // Scrollbar hidden for IE & Edge
  scrollbarWidth: 'none', // Scrollbar hidden for Firefox
  '&::-webkit-scrollbar': { display: 'none' } // Scrollbar hidden for Chrome, Safari & Opera
})

const MessageStream = ({
  messages = [],
  onEditClick,
  onAction,
  isLoading,
  isTyping,
  showShare,
  showLikes,
  onTypingFinish,
  onConsultedSourceClick,
  onFollowUpClick,
  chatHeight
}) => {
  const { t } = useTranslation()
  const {
    chat: { user, chatGPT, dougallMD }
  } = sections

  const [blankHeight, setBlankHeight] = useState(0)
  const scrollable = useRef(null)
  const currentPrompt = useRef(null)

  useEffect(() => {
    // Scroll to Top on messages reset (for mobile)
    if (messages.length === 2) {
      scrollable.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  useEffect(() => {
    if (isLoading) {
      currentPrompt.current?.scrollIntoView({
        behavior: 'smooth'
      })
    } else {
      setBlankHeight(0)
    }
  }, [isLoading])

  useEffect(() => {
    if (blankHeight > 0) {
      currentPrompt.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [blankHeight])

  /**
   * Blank Height Handler to mantain prompt on top
   */
  const handleAnswerResize = ({ answerHeight = 0 }) => {
    const newBlankHeight = chatHeight - answerHeight
    setBlankHeight(newBlankHeight < 0 ? 0 : newBlankHeight)
  }

  return (
    <StreamContainer data-testid="messages-stream" ref={scrollable}>
      {messages.map((messageInfo, idx) => {
        const {
          promptId = '',
          answerId = '',
          isAnswer,
          isDougall,
          showWarning,
          message,
          answer,
          hcpPrompt,
          editable,
          tags,
          urls,
          showSocial,
          isError,
          date,
          done,
          isDivider = false,
          consultedSources,
          followUpPrompts,
          relatedReports
        } = messageInfo

        let profileIcon

        if (isAnswer) {
          if (isDougall) {
            profileIcon = dougallMD?.defaultIcon
          } else {
            profileIcon = chatGPT?.defaultIcon
          }
        } else {
          profileIcon = user?.defaultIcon
        }

        let title

        if (isAnswer) {
          if (!isDougall) {
            title = t('answerFromGPT')
          }
        } else {
          title = t('you')
        }

        if (isDivider) {
          return <TextDivider key={idx} text={getDateChunk(date)} />
        }

        const consultedSourcesProps = isEmpty(consultedSources)
          ? null
          : {
              title: 'Consulted Sources Include',
              onTagClick: ({ ...props }) =>
                onConsultedSourceClick({
                  promptId,
                  answerId,
                  ...props
                }),
              tooltip: t('consultedSourcesTooltip'),
              message: { urls: consultedSources },
              hideLabels: true
            }

        return (
          <Box
            id={isAnswer ? `answer-${idx}` : `prompt-${idx}`}
            key={idx}
            sx={{ marginBottom: idx !== messages.length - 1 ? '5px' : '0px' }}
            ref={
              idx === messages.length - 1 && !isAnswer ? currentPrompt : null
            }
          >
            <Message
              index={`${idx}`}
              isAnswer={isAnswer}
              isDougall={isDougall}
              showWarning={showWarning}
              icon={profileIcon}
              title={title}
              message={message ?? answer}
              prompt={hcpPrompt}
              editable={editable}
              onEditClick={onEditClick}
              onAction={onAction}
              promptId={promptId}
              answerId={answerId}
              tags={tags}
              urls={urls}
              showSocial={showSocial}
              isError={isError}
              date={date}
              showShare={showShare}
              showLikes={showLikes}
              done={done}
              onResize={handleAnswerResize}
              onTypingFinish={onTypingFinish}
              consultedSourcesProps={consultedSourcesProps}
              followUpPrompts={followUpPrompts}
              onFollowUpClick={onFollowUpClick}
              relatedReports={relatedReports}
            />
          </Box>
        )
      })}
      {isLoading && !isTyping && (
        <Box display="flex" justifyContent="center" margin="10px 0px">
          <CircularProgress
            size={'55px'}
            thickness={4}
            sx={{ color: '#293056' }}
          />
        </Box>
      )}
      <Box
        height={blankHeight}
        sx={{ transition: `height ${blankHeight === 0 ? '.5s' : '0s'}` }}
      />
    </StreamContainer>
  )
}

MessageStream.propTypes = {
  /**
   * Messages to display
   */
  messages: PropTypes.array,
  /**
   * When is user prompt, on edit icon click
   */
  onEditClick: PropTypes.func,
  /**
   * When it has actionBar, on like or dislike
   */
  onAction: PropTypes.func,
  /**
   * Boolean when answer is fetching
   */
  isLoading: PropTypes.bool,
  /**
   * Boolean when an anwer is being typed
   */
  isTyping: PropTypes.bool,
  /**
   * Boolean to show Share Button or not
   */
  showShare: PropTypes.bool,
  /**
   * Boolean to show Like/Dislike Button
   */
  showLikes: PropTypes.bool,
  /**
   * Height of the Chat scroll view
   */
  chatHeight: PropTypes.number,
  /**
   * Function executed when current typewriter message animation finishes
   */
  onTypingFinish: PropTypes.func,
  /**
   * Function executed when clicking on a consulted source
   */
  onConsultedSourceClick: PropTypes.func,
  /**
   * On Follow Up Prompts Click
   */
  onFollowUpClick: PropTypes.func
}

export default MessageStream
